// src/components/CityModal.jsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import '../styles/CityModal.css';

const CityModal = ({ onClose, onSelectCity, currentCity }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Treat anything ≤900px as "mobile" → use portal bottom sheet
    const check = () => setIsMobile(window.innerWidth <= 900);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Lock body scroll when bottom sheet is open
  useEffect(() => {
    if (isMobile) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isMobile]);

  const popularCities = [
    { name: 'Delhi NCR',  image: '/delhi.png' },
    { name: 'Bangalore',  image: '/bangalore.png' },
    { name: 'Hyderabad',  image: '/hyderabad.png' },
    { name: 'Mumbai',     image: '/mumbai.png' },
    { name: 'Pune',       image: '/pune.png' },
    { name: 'Delhi',      image: '/delhi.png' },
    { name: 'Gurgaon',    image: '/gurgaon.png' },
    { name: 'Noida',      image: '/noida.png' },
    { name: 'Ahmedabad',  image: '/ahmedabad.png' },
    { name: 'Chennai',    image: '/chennai.png' },
    { name: 'Lucknow',    image: '/lucknow.png' },
    { name: 'Jaipur',     image: '/jaipur.png' },
    { name: 'Kolkata',    image: '/kolkata.png' },
  ];

  const mpDistrictCities = ['Indore', 'Mhow', 'Pithampur', 'Dewas', 'Ujjain'];

  const otherCities = [
    'Agartala','Ahmednagar','Ajmer','Akola','Alappuzha','Alibaug','Aligarh','Allahabad','Alwar','Ambala',
    'Amravati','Amritsar','Anand','Anantapur','Asansol','Aurangabad','Bareilly','Bardhaman','Belgaum',
    'Berhampur','Bhagalpur','Bharuch','Bhavnagar','Bhilai','Bhilwara','Bhiwani','Bhopal','Bhubaneswar',
    'Bhuj','Bidar','Bilaspur','Bokaro','Bulandshahr','Calicut','Cuttack','Darbhanga','Davanagere',
    'Dehradun','Dhanbad','Dharamshala','Dharwad','Dibrugarh','Durgapur','Erode','Firozabad','Gandhinagar',
    'Gaya','Ghaziabad','Gorakhpur','Guntur','Guwahati','Haldwani','Hampi','Hisar','Hoshiarpur','Hubli',
    'Indore','Itanagar','Jabalpur','Jalandhar','Jalgaon','Jammu','Jamnagar','Jamshedpur','Jhansi',
    'Jodhpur','Junagadh','Kakinada','Kanchipuram','Kannur','Kanpur','Karnal','Karur','Kharagpur',
    'Kolhapur','Kollam','Kota','Kozhikode','Latur','Lonavala','Madurai','Mangalore','Meerut',
    'Moradabad','Mysuru','Nagpur','Nanded','Nashik','Nellore','Patiala','Patna','Pollachi',
    'Puducherry','Raipur','Rajahmundry','Rajkot','Ranchi','Ratlam','Rewari','Rohtak','Rourkela',
    'Salem','Satna','Shimla','Siliguri','Solapur','Surat','Thane','Thrissur','Tirupati','Trichy',
    'Trivandrum','Tumkur','Udaipur','Ujjain','Vadodara','Varanasi','Vellore','Vijayawada','Visakhapatnam','Warangal',
  ];

  const groupedCities = otherCities.reduce((acc, city) => {
    const letter = city[0].toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(city);
    return acc;
  }, {});

  const allCities = [...popularCities.map(c => c.name), ...mpDistrictCities, ...otherCities];
  const filteredCities = allCities.filter(city =>
    city.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const isSearching = searchQuery.trim() !== '';

  const highlightMatch = (name) => {
    if (!searchQuery) return name;
    const regex = new RegExp(`(${searchQuery})`, 'gi');
    return name.replace(regex, `<span class="highlight-text">$1</span>`);
  };

  const modalContent = (
    <div className="city-modal-container">
      {/* Drag-handle pill — only visible as bottom sheet */}
      <div className="city-modal-drag-handle" />

      <div className="city-modal-header">
        <h2 className="city-modal-title">Select your city</h2>
        <button className="city-modal-close" onClick={onClose} aria-label="Close">✕</button>
      </div>

      <div className="city-search-container">
        <input
          type="text"
          className="city-search-input"
          placeholder="Search for your city"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <span className="city-search-icon">🔍</span>
      </div>

      {isSearching ? (
        <div className="search-results">
          {filteredCities.length > 0 ? (
            filteredCities.map((city, idx) => (
              <div
                key={idx}
                className={`more-city-item ${currentCity === city ? 'active' : ''}`}
                onClick={() => onSelectCity(city)}
                dangerouslySetInnerHTML={{ __html: highlightMatch(city) }}
              />
            ))
          ) : (
            <p className="no-results">No cities found</p>
          )}
        </div>
      ) : (
        <>
          <div className="city-section-title mp-district-title">
            📍 Available Locations in Madhya Pradesh
          </div>
          <div className="mp-district-section">
            <div className="more-cities-list">
              {mpDistrictCities.map((city, idx) => (
                <div
                  key={idx}
                  className={`more-city-item mp-city-item ${currentCity === city ? 'active' : ''}`}
                  onClick={() => onSelectCity(city)}
                >
                  {city}
                  <span className="available-badge">✓ Available</span>
                </div>
              ))}
            </div>
          </div>

          <div className="city-section-title">Popular Cities</div>
          <div className="city-grid">
            {popularCities.map((city, idx) => (
              <div
                key={idx}
                className={`city-card ${currentCity === city.name ? 'active' : ''}`}
                onClick={() => onSelectCity(city.name)}
              >
                <div className="city-icon-circle">
                  <img src={city.image} alt={city.name} className="city-image" />
                </div>
                <div className="city-name">{city.name}</div>
              </div>
            ))}
          </div>

          <div className="city-section-title">Other Cities</div>
          <div className="alphabetical-cities">
            {Object.keys(groupedCities).sort().map(letter => (
              <div key={letter} className="city-letter-section">
                <h3 className="city-letter">{letter}</h3>
                <div className="more-cities-list">
                  {groupedCities[letter].map((city, idx) => (
                    <div
                      key={idx}
                      className={`more-city-item ${currentCity === city ? 'active' : ''}`}
                      onClick={() => onSelectCity(city)}
                    >
                      {city}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // ── MOBILE / TABLET ≤900px : portal renders into <body> as bottom sheet ──
  if (isMobile) {
    return ReactDOM.createPortal(
      <>
        <div className="city-modal-backdrop" onClick={onClose} />
        {modalContent}
      </>,
      document.body
    );
  }

  // ── DESKTOP >900px : inline dropdown (original behaviour) ──
  return modalContent;
};

export default CityModal;